import { Link } from 'react-router-dom';
import DarkModeToggle from './DarkModeToggle';

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-black dark:to-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <img src="/apple-touch-icon.png" alt="EightyThreeLabs" className="w-8 h-8" />
              <span className="text-xl font-semibold">
                <span className="text-brand-primary">EightyThree</span>
                <span className="text-black dark:text-white">Labs</span>
              </span>
            </Link>
            
            {/* Dark Mode Toggle */}
            <DarkModeToggle />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white dark:bg-gray-900 rounded-3xl p-12 shadow-xl">
          <Link 
            to="/" 
            className="inline-flex items-center text-brand-primary hover:text-green-700 dark:hover:text-green-400 mb-8 transition-colors"
          >
            ← Back to Home
          </Link>
          
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8 text-center">About Us</h1>
          
          <div className="prose max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Our Mission</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              EightyThreeLabs is dedicated to creating engaging brain training games that challenge and improve cognitive abilities. We believe that mental fitness is just as important as physical fitness, and our games are designed to make brain training fun, accessible, and effective.
            </p>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">What We Do</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">We develop interactive puzzle and mathematical games that help users:</p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-6 space-y-2">
              <li>Improve mathematical skills and mental arithmetic</li>
              <li>Enhance logical thinking and problem-solving abilities</li>
              <li>Build concentration and focus through engaging challenges</li>
              <li>Track progress and maintain daily practice habits</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Our Games</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">Our current collection includes:</p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-6 space-y-2">
              <li><strong>Daily Math Challenge:</strong> Mixed difficulty arithmetic problems to sharpen your math skills</li>
              <li><strong>Sum Grid Puzzle:</strong> Logic puzzles that combine number placement with target sum objectives</li>
              <li><strong>Multiplication Grid:</strong> Strategic puzzles focused on multiplication patterns</li>
              <li><strong>Combined Grid Challenge:</strong> Advanced puzzles that require both addition and multiplication mastery</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Why Brain Training Matters</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Regular mental exercise has been shown to improve cognitive function, enhance memory, and maintain mental sharpness. Our games are designed to provide this exercise in an enjoyable format that fits into busy daily schedules.
            </p>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Our Commitment</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">We are committed to:</p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 mb-6 space-y-2">
              <li>Creating high-quality, educational content</li>
              <li>Protecting user privacy and data</li>
              <li>Providing accessible games for all skill levels</li>
              <li>Continuously improving our platform based on user feedback</li>
            </ul>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Contact Us</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              Have questions, suggestions, or feedback? We'd love to hear from you! Visit our <Link to="/" className="text-brand-primary hover:text-green-700 dark:hover:text-green-400 transition-colors">main website</Link> to get in touch through our newsletter signup or contact forms.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 py-8 mt-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-4">
            <p className="text-gray-600 dark:text-gray-200 mb-2">
              Made by The <span className="text-brand-primary font-semibold">EightyThree</span><span className="font-semibold dark:text-white">Labs</span> Team
            </p>
          </div>
          
          {/* Legal Links */}
          <div className="flex flex-wrap justify-center gap-4 mb-4 text-sm">
            <Link 
              to="/about" 
              className="text-gray-600 dark:text-gray-200 hover:text-brand-primary transition-colors"
            >
              About Us
            </Link>
            <Link 
              to="/privacy-policy" 
              className="text-gray-600 dark:text-gray-200 hover:text-brand-primary transition-colors"
            >
              Privacy Policy
            </Link>
            <Link 
              to="/terms-of-service" 
              className="text-gray-600 dark:text-gray-200 hover:text-brand-primary transition-colors"
            >
              Terms of Service
            </Link>
            <Link 
              to="/cookie-policy" 
              className="text-gray-600 dark:text-gray-200 hover:text-brand-primary transition-colors"
            >
              Cookie Policy
            </Link>
          </div>
          
          <div className="text-center text-xs text-gray-500 dark:text-gray-300">
            © 2025 EightyThreeLabs. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;